import {IconButton, MenuItem, Paper} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import {Scope} from 'Shared/constants/Scope';
import {hasAccessToScopeByApplication} from 'Shared/utilities/applicationId';
import AgentPortalEntry from 'Users/components/UserAdministration/UserEntry/Agent/AgentPortalEntry';
import AgentSupportUserTypeEntry from 'Users/components/UserAdministration/UserEntry/Agent/AgentSupportUserTypeEntry';
import FieldSalesLeaderEntry from 'Users/components/UserAdministration/UserEntry/Agent/FieldSalesLeaderEntry';
import FieldVicePresidentUserTypeEntry from 'Users/components/UserAdministration/UserEntry/Agent/FieldVicePresidentUserTypeEntry';
import VIPUserTypeEntry from 'Users/components/UserAdministration/UserEntry/Agent/VipUserTypeEntry';
import {changeAgentRole, removeUserType} from 'Users/ducks/userEntry';
import {Application} from 'Users/models/Application';
import {AgentRole, InvalidAgentUserType, matchAgentUserType, ValidAgentUserType} from 'Users/models/User/AgentUserType';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './AgentUserTypeEntry.sass';

type AgentUserTypeEntryProps = {
  agentUserType: InvalidAgentUserType | ValidAgentUserType;
  isEdit: boolean;
  isExternal: boolean;
};

const AgentUserTypeEntry = (props: AgentUserTypeEntryProps) => {
  const dispatch = useDispatch();
  const {agentUserType, isExternal} = props;
  const {agentRole} = agentUserType.value;

  const scopes = useSelector(selectUserScopes);
  const hasUpdateAccess = hasAccessToScopeByApplication(scopes, Scope.UpdateUser, Application.Agent);

  return (
    <Paper className="agent-user-type-entry">
      <IconButton
        className="agent-user-type-entry__remove-button"
        onClick={() => dispatch(removeUserType(agentUserType))}
        disabled={!hasUpdateAccess && props.isEdit}
      >
        <Close />
      </IconButton>
      <p className="agent-user-type-entry__title">Add Agent Permissions</p>
      <p className="agent-user-type-entry__description">
        Select a role and complete the appropriate fields to add agent permissions to this user.
      </p>
      <OutlinedSelect
        className="agent-user-type-entry__role-select"
        label="Agent Role"
        value={agentRole || ''}
        onChange={e => {
          dispatch(changeAgentRole(parseInt(e.target.value, 10) as AgentRole));
        }}
        disabled={!hasUpdateAccess && props.isEdit}
      >
        <MenuItem value={AgentRole.VIP} disabled={isExternal}>
          VIP
        </MenuItem>
        <MenuItem value={AgentRole.Agent}>Agent</MenuItem>
        <MenuItem value={AgentRole.AgentSupport}>Agent Support</MenuItem>
        <MenuItem value={AgentRole.FieldVicePresident} disabled={isExternal}>
          Field Vice President
        </MenuItem>
        <MenuItem value={AgentRole.FieldSalesLeader} disabled={isExternal}>
          Field Sales Leader
        </MenuItem>
      </OutlinedSelect>
      {matchAgentUserType(agentUserType.value, {
        Undetermined: (): React.ReactNode => null,
        VIP: vipUserType => <VIPUserTypeEntry vipUserType={vipUserType} />,
        FieldSalesLeader: fieldSalesLeaderUserType => (
          <FieldSalesLeaderEntry
            fieldSalesLeaderUserType={fieldSalesLeaderUserType}
            isEdit={props.isEdit}
            hasUpdateAccess={hasUpdateAccess}
          />
        ),
        Agent: agentUserType => (
          <AgentPortalEntry agentUserType={agentUserType} isEdit={props.isEdit} hasUpdateAccess={hasUpdateAccess} />
        ),
        AgentSupport: agentSupportUserType => (
          <AgentSupportUserTypeEntry
            agentSupportUserType={agentSupportUserType}
            isEdit={props.isEdit}
            hasUpdateAccess={hasUpdateAccess}
          />
        ),
        FieldVicePresident: fieldVicePresidentUserType => (
          <FieldVicePresidentUserTypeEntry
            fieldVicePresidentUserType={fieldVicePresidentUserType}
            isEdit={props.isEdit}
            hasUpdateAccess={hasUpdateAccess}
          />
        ),
      })}
    </Paper>
  );
};

export default AgentUserTypeEntry;
